/**
 * SessionsDevicesSection - секция «Сессии и устройства».
 *
 * Свои сессии за сегодня (сессия действует до конца дня) через /api/v2/profile/sessions:
 * пользователь определяется сервером по сессии, токены наружу не отдаются.
 * Текущее устройство помечается «Это устройство» и завершается только кнопкой «Выйти»;
 * вход администратора от имени пользователя помечается и завершается администратором.
 * Завершение одной или всех других сессий - после подтверждения.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button, IconButton, Chip, CircularProgress, Tooltip, Alert } from '@mui/material';
import { DevicesOutlined, LaptopMac, Smartphone, LogoutOutlined, Refresh, AdminPanelSettingsOutlined } from '@mui/icons-material';
import { ZERDE_COLORS } from '../constants';
import SectionHeaderDivider from './SectionHeaderDivider';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { ConfirmDialog } from '../../admin/reports/edit/EditCommon';
import { fetchSessions, revokeOtherSessions, revokeSession, ProfileSession } from '../profileApi';

type Confirm = { kind: 'one'; session: ProfileSession } | { kind: 'others'; count: number } | null;

// Раскладка - по ширине секции, а не окна: рядом с боковым меню профиля секция заметно уже окна.
// В широкой секции «Завершить» стоит справа от описания устройства, в узкой - под ним во всю ширину
const ROW_WIDE = '@container (min-width: 520px)';

/** «2026-10-22 14:05:31» -> «14:05» */
const timeOf = (value: string): string => {
  const match = value ? value.match(/[ T](\d{2}):(\d{2})/) : null;
  return match ? `${match[1]}:${match[2]}` : '—';
};

const SessionsDevicesSection: React.FC = () => {
  const [sessions, setSessions] = useState<ProfileSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSessions(await fetchSessions());
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить список сессий'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Свои сессии на других устройствах; вход администратора сюда не входит - его завершает администратор
  const others = sessions.filter(session => !session.isCurrent && !session.byAdministrator);

  const openConfirm = (next: Confirm) => {
    setConfirmError(null);
    setConfirm(next);
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    setConfirmError(null);
    try {
      if (confirm.kind === 'one') {
        notify.success(await revokeSession(confirm.session.id));
      } else {
        notify.success((await revokeOtherSessions()).message);
      }
      setConfirm(null);
      await load();
    } catch (err) {
      setConfirmError(apiErrorMessage(err, 'Не удалось завершить сессию'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Paper
      id="section-sessions"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px',
        containerType: 'inline-size'
      }}
    >
      <SectionHeaderDivider
        id="header-sessions"
        icon={<DevicesOutlined sx={{ fontSize: 22 }} />}
        title="Сессии и устройства"
        subtitle="Устройства, с которых сегодня выполнен вход в вашу учетную запись. Незнакомое устройство - завершите сессию и смените пароль"
        badge={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {others.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => openConfirm({ kind: 'others', count: others.length })}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, px: 1.5, py: 0.5 }}
              >
                Выйти на других устройствах
              </Button>
            )}
            <Tooltip title="Обновить список" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={load}
                  disabled={loading}
                  aria-label="Обновить список сессий"
                  sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', width: 34, height: 34 }}
                >
                  {loading ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ borderRadius: '12px' }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Повторить
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {loading && sessions.length === 0 && !loadError ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : sessions.length === 0 && !loadError ? (
        <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>
          Активных сессий за сегодня нет
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sessions.map(session => {
            const accent = session.isCurrent ? ZERDE_COLORS.primary : '#64748b';
            return (
              <Box
                key={session.id}
                sx={{
                  p: 2,
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: session.isCurrent ? '#bfdbfe' : '#e2e8f0',
                  bgcolor: session.isCurrent ? '#f0f7ff' : '#f8fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1.5,
                  [ROW_WIDE]: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexGrow: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '10px',
                      bgcolor: session.isCurrent ? 'rgba(21, 112, 239, 0.1)' : '#f1f5f9',
                      color: accent,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      position: 'relative'
                    }}
                  >
                    {session.mobile ? <Smartphone sx={{ fontSize: 22 }} /> : <LaptopMac sx={{ fontSize: 22 }} />}
                    {session.isOnline && (
                      <Tooltip title="В сети: активность за последние 3 минуты" arrow>
                        <Box
                          sx={{
                            position: 'absolute',
                            right: -2,
                            bottom: -2,
                            width: 11,
                            height: 11,
                            borderRadius: '50%',
                            bgcolor: ZERDE_COLORS.emerald,
                            border: '2px solid #ffffff'
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                        {session.device}
                      </Typography>
                      {session.isCurrent && (
                        <Chip
                          label="Это устройство"
                          size="small"
                          sx={{ bgcolor: ZERDE_COLORS.primary, color: '#ffffff', fontWeight: 600, fontSize: '0.7rem', height: 20, borderRadius: '6px' }}
                        />
                      )}
                      {session.byAdministrator && (
                        <Chip
                          icon={<AdminPanelSettingsOutlined sx={{ fontSize: 14 }} />}
                          label="Вход администратора"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20, borderRadius: '6px', color: '#9a3412', borderColor: '#fed7aa' }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8rem', display: 'block', mt: 0.4 }}>
                      IP {session.ip || '—'} · вход {timeOf(session.createdAt)} · активность {timeOf(session.lastActivityAt)}
                      {session.currentSection ? ` · раздел: ${session.currentSection}` : ''}
                    </Typography>
                  </Box>
                </Box>

                {!session.isCurrent && !session.byAdministrator && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<LogoutOutlined sx={{ fontSize: 16 }} />}
                    onClick={() => openConfirm({ kind: 'one', session })}
                    sx={{ flexShrink: 0, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderRadius: '8px', borderColor: '#fca5a5', bgcolor: '#ffffff' }}
                  >
                    Завершить
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm && confirm.kind === 'one' ? 'Завершить сессию?' : 'Выйти на других устройствах?'}
        confirmText="Завершить"
        confirmColor="error"
        maxWidth="xs"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => {
          if (!working) setConfirm(null);
        }}
      >
        <Typography variant="body2" sx={{ color: '#334155' }}>
          {confirm && confirm.kind === 'one'
            ? `На устройстве «${confirm.session.device}» (IP ${confirm.session.ip || '—'}) потребуется войти заново.`
            : confirm
              ? `Будет завершено сессий: ${confirm.count}. На этих устройствах потребуется войти заново. Текущее устройство останется в системе.`
              : ''}
        </Typography>
        {confirmError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
            {confirmError}
          </Alert>
        )}
      </ConfirmDialog>
    </Paper>
  );
};

export default SessionsDevicesSection;
